import type { ListChange } from "../data/store.js";
import type { Anchors, List } from "../lib/api.js";
import { uncheckedByList } from "../lib/counts.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icons.js";
import { stepAnchors } from "../lib/reorder.js";
import { listPath } from "../lib/router.js";
import { confirmDeleteList } from "./deleteList.js";
import { listActions } from "./listActions.js";
import { openShareList } from "./share.js";
import { actionMenu, actionsButton, SEPARATOR, type MenuItem } from "./menu.js";
import { MOVE_UNDO, moveLink, moveTarget, type MoveResult, type MoveTarget } from "./moveUndo.js";
import { feedbackLine, holdingPlace, type FeedbackLine } from "./feedback.js";
import { openNewList } from "./newList.js";
import { importRecoveries, openImport } from "./importList.js";
import { reorderController } from "./reorder.js";
import { reorderMode } from "./reorderMode.js";
import { sharedWithMe } from "./sharedWithMe.js";
import { KIND_ICON, addRow, banner, countBadge, listLabel, messageOf, setCount, unreachable, type Screen, type Shell } from "./ui.js";

/** The landing screen reads and writes only the local store. */
/** What an empty overview says (g56): what a list is for, in one line. */
export const LISTS_EMPTY = "Keep tasks, shopping and ideas in their own lists.";

export function listsScreen(shell: Shell): Screen {
  const { store } = shell;
  const rows = el("ul", { class: "rows" });
  const nodes = new Map<string, Row>();
  let counts = uncheckedByList(store.allItems());
  const status = el("div", { class: "status" });
  const setStatus = (node: Node | undefined) => status.replaceChildren(...(node ? [node] : []));
  const actionSlot = el("div", { class: "list-action-slot" });
  const menu = actionMenu();
  const actions = actionsButton();
  actions.classList.add("lists-actions");
  actions.setAttribute("aria-label", "Lists actions");
  // Reorder lists first on a phone (g53); Import opens a surface, which ends it.
  // Undo move joins Reorder's group (g102), only while there is a drop to
  // undo; it opens nothing, so the mode stays on through it.
  actions.addEventListener("click", () => menu.open(actions, (): MenuItem[] => {
    const undo: MenuItem[] = undoTarget.get() ? [{ label: MOVE_UNDO.label, icon: "undo", onSelect: undoMove }] : [];
    const ordering: MenuItem[] = [...mode.entry().filter(entry => entry !== SEPARATOR), ...undo];
    const rest = mode.leavingAll([{ label: "Import", icon: "inbox", onSelect: () => openImport(shell, actions) }]);
    return ordering.length ? [...ordering, SEPARATOR, ...rest] : rest;
  }));
  const recovery = importRecoveries(shell, actions);
  const footer = addRow("Add a new list", button => { mode.exit(); openNewList(shell, { returnFocusTo: button }); });
  // Routine feedback (g102), under the header and gone in a few seconds; the
  // Reorder bar says it instead while the mode is on.
  const feedback: FeedbackLine = feedbackLine({ around: change => holdingPlace(feedback.node, rows, change) });
  const say = (message: string) => {
    // One message on the screen: entering the mode clears this line, and
    // leaving it clears the bar's.
    if (mode.active()) mode.say(message);
    else feedback.show(message);
  };
  let disposed = false;
  // The rows and the add row below them share one gap owner, as a list does (g51).
  // An empty overview says what belongs here (g56), once, in the body rather
  // than the status slot: the add row below it is already the one way to act,
  // and `.status` stays for loading and unreachable, which are not "empty".
  const emptyLine = el("p", { class: "muted lists-empty", text: LISTS_EMPTY, attrs: { hidden: true } });
  const body = el("div", { class: "list-body", children: [rows, emptyLine, menu.node, footer] });
  const node = el("section", { class: "screen", children: [
    el("header", { class: "list-head", children: [el("div", { class: "list-title-row", children: [
      el("h1", { class: "list-title", text: "Lists" }), actions, actionSlot,
    ] })] }), status, feedback.node, recovery.node,
  ] });
  const reordering = reorderController({
    root: node, rows, footer, nodeFor: id => nodes.get(id)?.node,
    all: () => store.lists(), fallback: () => footer, announce: shell.announce,
    changedMessage: "The lists changed. Reorder cancelled.", move: commitMove,
    obstacles: () => [mode.bar],
  });
  body.insertBefore(reordering.marker, footer);
  const mode = reorderMode({
    shell, rows, noun: "lists", opener: actions, reordering,
    refreshMenu: () => { if (menu.isOpen()) menu.refresh(); },
    undo: () => undoMove(),
    entered: () => feedback.clear(),
  });
  /** The drop being saved, which becomes Undo move's once this device holds it. */
  let savingDrop: MoveTarget | undefined;
  const undoTarget = moveTarget(target => {
    mode.setUndo(target !== undefined);
    if (menu.isOpen()) menu.refresh();
  });
  // The recovered-import banner's buttons open surfaces too.
  recovery.node.addEventListener("click", event => {
    if (event.target instanceof Element && event.target.closest("button")) mode.exit();
  }, true);
  // The bar sits between the header's blocks and the rows, like a list's.
  node.append(mode.bar, body);
  // Below the account's own lists and the way to add one, a block of its own: the shared lists it added (g132).
  const shared = sharedWithMe(shell, { parent: node, menu, landing: () => footer, leaving: () => mode.exit() });

  function paintStatus(): void {
    emptyLine.hidden = true;
    if (nodes.size > 0) return setStatus(undefined);
    if (store.status() === "loading") return setStatus(el("p", { class: "muted", text: "Loading…" }));
    if (store.status() === "unreachable") return setStatus(unreachable(store));
    setStatus(undefined);
    emptyLine.hidden = false;
  }

  /** Preserve the row and its controls, including focus, while moving it. */
  function place(id: string, entry: Row): void {
    const order = store.lists();
    const at = order.findIndex(candidate => candidate.id === id);
    const next = order.slice(at + 1).find(candidate => nodes.has(candidate.id));
    const before = next ? nodes.get(next.id)!.node : null;
    if (entry.node.parentNode !== rows || entry.node.nextElementSibling !== before) {
      const focused = entry.node.contains(document.activeElement) ? document.activeElement as HTMLElement : undefined;
      rows.insertBefore(entry.node, before);
      focused?.focus({ preventScroll: true });
    }
    nodes.set(id, entry);
  }

  function apply(change: ListChange): void {
    const existing = nodes.get(change.id);
    if (!change.list) {
      if (existing) {
        // The same kind of control: the handle in Reorder mode (g53), else the ellipsis.
        const kind = existing.node.contains(document.activeElement) && document.activeElement?.closest(".item-handle")
          ? ".item-handle" : ".item-actions";
        const landing = existing.node.nextElementSibling?.querySelector<HTMLElement>(kind)
          ?? existing.node.previousElementSibling?.querySelector<HTMLElement>(kind) ?? footer;
        const anchored = existing.node.contains(menu.anchor() ?? null);
        if (anchored) menu.close();
        existing.dispose();
        const focused = existing.node.contains(document.activeElement);
        existing.node.remove();
        nodes.delete(change.id);
        // The drop Undo move held was this list's.
        if (undoTarget.get()?.id === change.id) undoTarget.set(undefined);
        if (savingDrop?.id === change.id) savingDrop = undefined;
        if (focused || anchored) landing.focus({ preventScroll: true });
      }
    } else {
      const entry = existing ?? row(change.list);
      entry.update(change.list);
      place(change.id, entry);
    }
    reordering.changed();
    refreshHandles();
    if (menu.isOpen()) menu.refresh();
    paintStatus();
  }

  function refreshHandles(): void {
    for (const entry of nodes.values()) entry.handle.setAttribute("aria-disabled", String(nodes.size < 2));
  }

  function paintCounts(): void {
    counts = uncheckedByList(store.allItems());
    for (const [id, entry] of nodes) {
      const list = store.list(id);
      if (list) entry.update(list);
    }
    if (menu.isOpen()) menu.refresh();
  }

  /**
   * The one place a list is moved. A drop becomes what Undo move undoes; a
   * step clears it, so Undo only ever undoes the latest move. It answers with
   * how the move went (g102), and `undo` leaves the position unsaid for the
   * caller's "Move undone.".
   */
  function commitMove(id: string, anchors: Anchors, options: { undoable?: boolean; undo?: boolean; reverses?: string } = {}): MoveResult {
    const all = store.lists();
    const at = all.findIndex(list => list.id === id);
    if (at < 0) return { status: "missing" };
    const back = { afterId: all[at - 1]?.id ?? null, beforeId: all[at + 1]?.id ?? null };
    let saved: Promise<boolean> | undefined;
    // The id this move is queued under, told before `saved` answers (g108).
    let made: string | undefined;
    try {
      saved = store.moveList(id, anchors, moveLink(options.reverses, (operationId) => { made = operationId; }));
    } catch (error) {
      setStatus(banner(messageOf(error)));
      return { status: "refused" };
    }
    if (!saved) return { status: "missing" };
    if (!options.undo) {
      const order = store.lists();
      shell.announce(`Moved ${store.list(id)!.name} to ${order.findIndex(list => list.id === id) + 1} of ${order.length}.`);
    }
    const target: MoveTarget | undefined = options.undoable ? { id, back } : undefined;
    // Offered, and said, once this device holds the drop, so an undo is never
    // queued behind a save that then fails. A later move leaves it behind.
    undoTarget.set(undefined);
    savingDrop = target;
    if (target) void saved.then(ok => {
      if (savingDrop !== target) return;
      savingDrop = undefined;
      if (!ok || disposed) return;
      if (made !== undefined) target.operationId = made;
      undoTarget.set(target);
      say(MOVE_UNDO.moved);
    });
    return { status: "applied", saved };
  }

  /** Undo move: "Move undone." is said for a move that was made and saved, and for nothing else. */
  function undoMove(): void {
    const target = undoTarget.get();
    if (!target) return;
    const result = commitMove(target.id, target.back, { undo: true, ...(target.operationId !== undefined ? { reverses: target.operationId } : {}) });
    if (result.status !== "applied") return;
    void result.saved.then(ok => {
      if (!ok || disposed) return;
      say(MOVE_UNDO.undone);
      shell.announce(MOVE_UNDO.undone);
    });
  }

  function row(initial: List): Row {
    let shown = initial;
    let glyph = icon(KIND_ICON[initial.kind]);
    const name = el("span", { class: "row-name", text: initial.name });
    const count = countBadge();
    const link = el("a", { class: "row", attrs: { href: listPath(initial.id), "data-link": true }, children: [
      el("span", { class: "list-kind", children: [glyph] }),
      el("div", { class: "row-body", children: [name] }), count,
    ] });
    const handle = el("button", { class: "item-handle", children: [icon("grip")], attrs: { type: "button", title: "Reorder" } });
    const actions = actionsButton();
    const rowNode = el("li", { class: "list-row", attrs: { "data-id": initial.id }, children: [handle, link, actions] });
    let question: { close(): void } | undefined;
    const management = listActions({
      shell, listId: initial.id, trigger: actions,
      prepareEdit: () => true, dirtyItem: () => false,
      share: () => openShareList(shell, shown.id, actions),
      clear: ids => store.clearCompleted(initial.id, ids), afterClear: () => {},
      problem: message => setStatus(banner(message)),
      settled: () => { if (menu.isOpen()) menu.refresh(); },
      deleteList: () => {
        const next = rowNode.nextElementSibling?.querySelector<HTMLElement>(".item-actions");
        const previous = rowNode.previousElementSibling?.querySelector<HTMLElement>(".item-actions");
        question = confirmDeleteList(shell, shown.id, {
          dirty: false, cancel: () => { question = undefined; },
          remove: () => { question = undefined; store.removeList(shown.id); },
          returnFocusTo: () => [actions, next, previous].find(control => control?.isConnected) ?? footer,
        });
      },
    });
    const step = (by: -1 | 1) => {
      if (reordering.active()) return;
      const anchors = stepAnchors(store.lists(), shown.id, by);
      if (anchors) commitMove(shown.id, anchors);
    };
    const entries = (): MenuItem[] => [
      { label: "Move up", icon: "up", disabled: !stepAnchors(store.lists(), shown.id, -1), onSelect: () => step(-1) },
      { label: "Move down", icon: "down", disabled: !stepAnchors(store.lists(), shown.id, 1), onSelect: () => step(1) },
      SEPARATOR, ...mode.leavingAll(management.entries()),
    ];
    actions.addEventListener("click", () => menu.open(actions, entries));
    handle.addEventListener("keydown", event => {
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
      event.preventDefault();
      step(event.key === "ArrowUp" ? -1 : 1);
      handle.focus({ preventScroll: true });
    });
    reordering.bind(handle, () => shown.id);
    const update = (list: List) => {
      if (list.kind !== shown.kind) { const next = icon(KIND_ICON[list.kind]); glyph.replaceWith(next); glyph = next; }
      shown = list;
      name.textContent = list.name;
      setCount(count, counts.get(list.id) ?? 0);
      link.setAttribute("aria-label", listLabel(list, counts.get(list.id) ?? 0));
      handle.setAttribute("aria-label", `Reorder ${list.name}`);
      actions.setAttribute("aria-label", `Actions for ${list.name}`);
      management.refresh();
    };
    update(initial);
    return { node: rowNode, handle, update, dispose: () => { management.dispose(); question?.close(); } };
  }

  for (const list of store.lists()) place(list.id, row(list));
  refreshHandles();
  paintStatus();
  const stopLists = store.onLists(apply);
  const stopItems = store.onAllItems(paintCounts);
  const stopStatus = store.onStatus(paintStatus);
  return {
    node, update() {},
    action: () => ({ label: "New list", slot: actionSlot, activate: mode.leaving(() => openNewList(shell)) }),
    dispose() {
      disposed = true;
      savingDrop = undefined;
      undoTarget.set(undefined);
      feedback.dispose();
      recovery.dispose();
      reordering.dispose(); mode.dispose(); menu.close();
      for (const row of nodes.values()) row.dispose();
      stopLists(); stopItems(); stopStatus(); shared.dispose();
    },
  };
}

interface Row {
  node: HTMLElement;
  handle: HTMLButtonElement;
  update(list: List): void;
  dispose(): void;
}
